import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Card, Table, Button, Form, InputGroup, Badge, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getUsers, updateUser, deleteUser, resetUserPassword } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import PaginationSearch from "../pagination/PaginationSearch";

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // 🔥 debounce state
  const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [resetId, setResetId] = useState(null);

  const { data: usersData, isLoading, error, refetch } = useQuery(
    ['users', currentPage, debouncedSearchTerm, limit],
    () => getUsers({ page: currentPage, search: debouncedSearchTerm, limit: limit }),
    { keepPreviousData: true }
  );

  // 🔥 debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // reset ke halaman 1 tiap kali search baru
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  
  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (id) => {
    setResetId(id);
    setShowResetModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteUser(deleteId);
      toast.success('Pengguna berhasil dihapus');
      refetch();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Gagal menghapus pengguna');
    }
  };

  const confirmResetPassword = async () => {
    try {
      await resetUserPassword(resetId);
      toast.success('Password berhasil direset');
      setShowResetModal(false);
    } catch (error) {
      toast.error('Gagal mereset password');
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      await updateUser(selectedUser.id, formData);
      toast.success('Data pengguna berhasil diperbarui');
      refetch();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Gagal memperbarui data pengguna');
    }
  };

  if (isLoading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">Error: {error.message}</div>;

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Manajemen Pengguna</h2>
      </div>

      <Card>
        <Card.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Cari pengguna..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>

          <Table responsive striped hover>
            <thead>
              <tr>
                <th>No</th>
                <th>Nama Lengkap</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {usersData?.data?.map((user, index) => (
                <tr key={user.id}>
                  <td>{(currentPage - 1) * limit + index + 1}</td>
                  <td>{user.full_name}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <Badge 
                      className="p-2 col-10"
                      bg={
                        user.role === 'admin' ? 'danger' : 
                        user.role === 'pengguna' ? 'primary' : 'warning'
                      }>
                      {user.role}
                    </Badge>
                  </td>
                  <td>
                    <Badge 
                      className="p-2 col-10"
                      bg={user.is_active ? 'success' : 'secondary'}>
                      {user.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </td>
                  <td>
                    <div className='group-this-items'>

                    <Button
                      size="sm"
                      variant="info"
                      className="me-3 action-btn"
                      onClick={() => handleEdit(user)}
                      disabled={user.id === currentUser?.id}
                      >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="warning"
                      className="me-3 action-btn"
                      onClick={() => handleResetPassword(user.id)}
                      disabled={user.id === currentUser?.id}
                      >
                      <i className="fas fa-key"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      className="action-btn"
                      onClick={() => handleDelete(user.id)}
                      disabled={user.id === currentUser?.id}
                      >
                      <i className="fas fa-trash"></i>
                    </Button>
                      </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {usersData?.pagination && (
            <div className="mt-2">
              <PaginationSearch
                pagination={usersData.pagination}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={setLimit}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pengguna</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditUserForm 
            user={selectedUser} 
            onSubmit={handleEditSubmit}
            onCancel={() => setShowEditModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Apakah Anda yakin ingin menghapus pengguna ini?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Hapus
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Apakah Anda yakin ingin mereset password pengguna ini?
          <br />
          <small className="text-muted">
            Password akan direset menjadi "password123"
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Batal
          </Button>
          <Button variant="warning" onClick={confirmResetPassword}>
            Reset Password
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Edit User Form Component
const EditUserForm = ({ user, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      full_name: user?.full_name || '',
      email: user?.email || '',
      role: user?.role || '',
      is_active: user?.is_active || true
    }
  });

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Form.Group className="mb-3">
        <Form.Label>Nama Lengkap *</Form.Label>
        <Form.Control
          type="text"
          {...register('full_name', { required: 'Nama lengkap wajib diisi' })}
          isInvalid={!!errors.full_name}
        />
        <Form.Control.Feedback type="invalid">
          {errors.full_name?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email *</Form.Label>
        <Form.Control
          type="email"
          {...register('email', { 
            required: 'Email wajib diisi',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Email tidak valid'
            }
          })}
          isInvalid={!!errors.email}
        />
        <Form.Control.Feedback type="invalid">
          {errors.email?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Role *</Form.Label>
        <Form.Select
          {...register('role', { required: 'Role wajib dipilih' })}
          isInvalid={!!errors.role}
        >
          <option value="">Pilih Role</option>
          <option value="admin">Admin</option>
          <option value="pengguna">Pengguna</option>
          <option value="peninjau">Peninjau</option>
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {errors.role?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Aktif"
          {...register('is_active')}
        />
      </Form.Group>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Batal
        </Button>
        <Button variant="primary" type="submit">
          Simpan
        </Button>
      </div>
    </Form>
  );
};

export default UserManagement; 